import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { communityApi } from "@bw/shared/api";
import type { CommunityAdmissionListResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * The caller's persisted community admissions. Reads and transitions remain
 * server-authoritative; successful transitions invalidate the hub/detail slots
 * so membership and admission status never flip optimistically.
 */
export function useCommunityAdmissions(options: { enabled?: boolean } = {}) {
  const queryClient = useQueryClient();
  const admissions = useQuery<CommunityAdmissionListResponse>({
    queryKey: queryKeys.social.admissionsMine(),
    queryFn: () => communityApi.listMyAdmissions({ status: "pending" }),
    enabled: options.enabled ?? true,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.social.admissionsMine(),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.social.all });
  };

  const accept = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (admissionId: string) =>
      communityApi.acceptInvitation(admissionId),
    onSuccess: invalidate,
  });
  const decline = useMutation({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (admissionId: string) =>
      communityApi.declineInvitation(admissionId),
    onSuccess: invalidate,
  });

  return { admissions, accept, decline };
}
